/** Phase-1 content: Dad's chat, English practice, TV news, social-media safety. */
export const enExtra = {
  dadChat: {
    name: 'Dad 👔',
    preview: 'Dad sent a picture',
    /** Big text drawn on the greeting cards Dad sends (one per day, cycling). */
    cards: [
      { big: 'Good Morning', sub: 'Smile today, and the world smiles back' },
      { big: 'Peace & Joy', sub: 'Wishing you health and good luck' },
      { big: 'Happy Friday', sub: 'Work hard, rest well' },
      { big: 'Have a lovely weekend', sub: 'Family is the greatest treasure' },
      { big: 'Good Evening', sub: 'Rest well, dream sweet' },
      { big: 'Stay Healthy', sub: 'Drink water, walk often' },
      { big: 'Blessings', sub: 'May all go smoothly for you' },
      { big: 'Good Morning', sub: 'A new day, new hope' },
    ],
    cardCaption: ['🌸 For my dear family!', '☀️ Have a nice day, everyone!', '🙏 Blessings to you!', '💪 Keep going!'],
    choices: {
      thanks: 'Thanks, Dad! ❤️',
      cardBack: 'Send a greeting card back 🌻',
      askDay: 'How’s work today, Dad?',
      photo: 'Send Dad a photo from today 📸',
    },
    replies: {
      thanks: ['You’re welcome! Dad loves you!', 'Hehe, Dad is happy you liked it!', 'Remember to eat your vegetables! 🥦'],
      cardBack: ['Wow! So beautiful! Where did you find it? 😄', 'Dad saved it! Now I have to send it to Grandpa too.', 'Two cards in one day! Lucky!'],
      askDay: ['Busy, but your message makes it better!', 'Many meetings… thinking about dinner with you all.', 'Boss said good job today! 💪'],
      photo: ['What a great photo! You’re a real photographer!', 'Looks like a fun day. Be careful out there!', 'Dad set it as my phone background!'],
    },
    /** Rumours elders often forward — the player learns to correct them kindly. */
    rumors: [
      {
        text: '⚠️ URGENT!! Forward this to 10 people or your phone will be locked tomorrow!!!',
        correct: 'Dad, that’s a chain message. Phones don’t get locked like that 😊',
        share: 'OK, I’ll forward it!',
        dadOk: 'Oh! Really? Thank you for telling me. I won’t forward it.',
        dadBad: 'Great, I already sent it to 30 people! 😅',
        lesson: 'Chain messages that threaten bad luck are always fake. Don’t forward them.',
      },
      {
        text: 'Doctor says: drinking hot lemon water every hour cures all viruses! Share with everyone!',
        correct: 'Dad, let’s check a reliable health website first. One drink can’t cure all viruses.',
        share: 'Wow, I’ll tell my friends!',
        dadOk: 'You’re right, I didn’t check where it came from. Smart kid!',
        dadBad: 'Yes! I’ll make lemon water for the whole office!',
        lesson: 'Health “miracle cures” spread fast. Check official sources and ask a doctor.',
      },
      {
        text: 'Free! Click this link to get NT$5000 from the government. Just enter your bank card!',
        correct: 'Dad, don’t click! The government never asks for your card in a message.',
        share: 'Free money? Let’s click!',
        dadOk: 'Thank goodness you told me. I’ll delete it right now.',
        dadBad: 'Hmm, the page asked for my password… that’s strange.',
        lesson: 'Real offices never ask for card numbers or passwords by message. It’s phishing.',
      },
    ],
    rumorPrompt: 'Dad forwarded a message:',
  },

  english2: {
    title: '📚 English corner',
    tabs: { learn: 'Learn', review: 'Review', spell: 'Spell', talk: 'Talk', read: 'Read' },
    learnHint: 'Tap a word to hear it. Practice adds it to your review deck.',
    reviewHint: 'Think of the English word, then reveal it.',
    reviewNone: 'Nothing to review right now — great job! Learn new words first.',
    reviewDue: (n: number) => `${n} word${n === 1 ? '' : 's'} to review`,
    show: 'Show the answer',
    knew: '✅ I knew it',
    again: '🔁 Again',
    choose: 'Which word means this?',
    spellHint: 'Listen, then tap the letters in order.',
    spellOk: 'Perfect spelling!',
    spellBad: 'Not quite — try again!',
    undo: '⌫',
    hear: '🔊 Hear it',
    next: 'Next ➜',
    talkHint: 'Pick the best English reply. Listen to each line!',
    talkPick: 'Choose a scenario:',
    talkGood: 'Great reply!',
    talkBad: 'Hmm, that doesn’t fit. Try another one.',
    talkDone: (title: string) => `Conversation complete: ${title}`,
    hereFirst: '📍 Here',
    mastered: (n: number, total: number) => `Mastered ${n}/${total}`,
    categories: {
      school: 'School', library: 'Library', food: 'Food & café', shopping: 'Shopping', sports: 'Sports',
      health: 'Health', home: 'Home', feelings: 'Feelings', safety: 'Online safety', nature: 'Nature',
    },
    /** Short goal for each English role-play scenario (the dialogue itself is in English). */
    scenarios: {
      cafe: 'Order a drink at a café',
      library: 'Borrow a book',
      store: 'Buy a snack at the store',
      nurse: 'Tell the nurse how you feel',
      directions: 'Ask for directions',
      newFriend: 'Introduce yourself to a new friend',
      restaurant: 'Order dinner at a restaurant',
      sports: 'Join a team practice',
      gift: 'Choose a birthday gift',
      phone: 'Answer a call politely',
    },
    meanings: {
      classroom: 'classroom', homework: 'homework', teacher: 'teacher', pencil: 'pencil', eraser: 'eraser', schedule: 'timetable',
      borrow: 'to borrow', return: 'to give back', quiet: 'quiet', novel: 'novel (book)', dictionary: 'dictionary', shelf: 'shelf',
      menu: 'menu', order: 'to order', delicious: 'delicious', thirsty: 'thirsty', bill: 'the bill', breakfast: 'breakfast',
      price: 'price', cheap: 'cheap', expensive: 'expensive', receipt: 'receipt', discount: 'discount', cashier: 'cashier',
      team: 'team', practice: 'practice', score: 'score', swim: 'to swim', racket: 'racket', referee: 'referee',
      fever: 'fever', headache: 'headache', medicine: 'medicine', rest: 'rest', nurse: 'nurse', bandage: 'bandage',
      kitchen: 'kitchen', laundry: 'laundry', chores: 'housework', neighbor: 'neighbour', family: 'family', dinner: 'dinner',
      nervous: 'nervous', proud: 'proud', grateful: 'grateful', lonely: 'lonely', excited: 'excited', calm: 'calm',
      password: 'password', privacy: 'privacy', stranger: 'stranger', report: 'to report', block: 'to block', scam: 'scam',
      blossom: 'blossom', pond: 'pond', breeze: 'breeze', butterfly: 'butterfly', sunset: 'sunset', puddle: 'puddle',
    },
  },

  news: {
    title: '📺 Town News 24',
    live: 'LIVE',
    anchor: 'Anchor',
    quiz: 'Real news or fake news?',
    real: '✅ Real',
    fake: '❌ Fake',
    right: 'Correct!',
    wrong: 'Not quite!',
    nextStory: 'Next story ▶',
    done: 'That’s all for today. Thanks for watching!',
    ticker: 'Weather: sunny with a light breeze · Remember: check the source before you share ·',
    stories: [
      { head: 'Cherry blossoms bloom early in Town Park', body: 'Warm weather brought the cherry trees into bloom a week early. Families are enjoying picnics under the pink trees.', real: true, why: 'This comes from the local news team with photos and a date. Normal, checkable news.' },
      { head: 'Shocking! Eating chocolate makes you grow 10 cm in a week!', body: 'A video online claims a “secret chocolate diet” makes kids taller overnight.', real: false, why: 'Extreme promises, no doctors named, and a “secret” trick — classic fake-news signs.' },
      { head: 'School library adds 500 new books', body: 'The middle school library received new comics, novels and science books thanks to a charity sale.', real: true, why: 'Specific, ordinary facts that the school can confirm.' },
      { head: 'Celebrity giving away free phones — just share this post!', body: 'A post says a famous singer will send a phone to everyone who shares and comments.', real: false, why: 'Giveaways that require sharing are engagement scams. Real brands announce on official pages.' },
      { head: 'Bus line 8 timetable changes next month', body: 'The city announced buses will run every 10 minutes during school hours.', real: true, why: 'Announced by the city transport office — you can check their website.' },
      { head: 'Scientists say the moon will turn green tonight', body: 'A viral message claims a rare green moon will appear for the first time in 1000 years.', real: false, why: 'No scientist or observatory is named, and it plays on excitement. Check an astronomy site.' },
      { head: 'Police warn about fake delivery text messages', body: 'Messages saying “your parcel is waiting, pay here” are a new scam. Don’t click the links.', real: true, why: 'An official warning — and good advice: never pay through a link in a text.' },
      { head: 'Drinking cola cures colds, says anonymous expert', body: 'A chain message says cola is better than medicine for a cold.', real: false, why: '“Anonymous expert” is a red flag. Medical advice should come from real doctors.' },
      { head: 'Town Park pond gets new lily pads and fish', body: 'Volunteers planted lily pads and released local fish to keep the pond healthy.', real: true, why: 'A local event with named volunteers and photos.' },
    ],
  },

  social: {
    snapshot: '📸 Take a photo',
    upload: '🖼️ Upload a photo',
    captionTitle: 'Add a caption',
    captions: ['Beautiful day! ☀️', 'Look at my snack 🍙', 'With my friends 💛', 'My pet is so cute 🐰', 'Study time 📚'],
    posted: 'Posted! Your friends will see it.',
    likes: (n: number) => `❤️ ${n}`,
    comments: 'Comments',
    writeComment: 'Comment',
    you: 'You',
    friendComments: {
      0: ['So pretty! 😍', 'Where is that?', 'Perfect weather!'],
      1: ['Yum! Save me one!', 'I’m hungry now 😂', 'Looks delicious!'],
      2: ['Miss you guys!', 'Best friends! 💛', 'Next time invite me!'],
      3: ['Sooo cute!! 🥰', 'What’s its name?', 'I want a pet too!'],
      4: ['You work so hard! 💪', 'Study buddy?', 'Good luck on the test!'],
    } as Record<number, string[]>,
    replyOptions: ['Thank you! 😊', 'Haha yes!', 'Let’s go together next time!'],
    /** Friends' answers to what you type, picked by what your comment is about. */
    replies: {
      question: ['Good question! I think so 🤔', 'Hmm, let me ask my mom and tell you!', 'Yes! Want to come next time?'],
      thanks: ['You’re welcome! 🥰', 'Anytime, friend! 💛', 'No problem!'],
      laugh: ['Hahaha same 😂', 'Stop, I’m laughing in class! 🤣', 'LOL!'],
      love: ['Aww, love you too! 💖', 'You’re the best! 🥰', 'Best friends forever!'],
      food: ['Now I’m hungry 🤤', 'Save me a bite!', 'That looks so yummy!'],
      plan: ['Yes! Let’s do it this weekend!', 'Count me in! 🙌', 'I’ll ask my parents first!'],
      sad: ['Oh no, are you okay? 🫂', 'Want to talk later? I’m here.', 'Sending you a big hug!'],
      generic: ['Nice! 😊', 'Totally agree!', 'Haha, you’re funny!', 'Cool! ✨'],
    },
    friendNames: ['Rain', 'Emma', 'Jay', 'Ming'],
    lessonTitle: '🛡️ What did we learn?',
    cases: {
      fakeFriend: {
        author: '“Mia, 13 ✨”',
        tag: '⚠️ Friend request',
        content: 'Hi!! You look so cool! I’m new here. Can we chat privately? What school do you go to? 🥺',
        safe: 'Don’t share — ask a parent first',
        risky: 'Tell her my school and class',
        safeMsg: 'Well done. You can’t know who is really behind a profile. Never share your school, address or schedule.',
        riskyMsg: 'Careful! Profiles can be fake — adults sometimes pretend to be kids. Keep personal info private.',
        lesson: 'New online “friends” who ask lots of personal questions fast are a warning sign. Ask a trusted adult.',
      },
      romance: {
        author: '“Leo 💕”',
        tag: '⚠️ Message',
        content: 'I think I really like you. You’re special. Don’t tell your parents about us, ok? Can you buy me a gift card?',
        safe: 'Block and tell a trusted adult',
        risky: 'Keep it secret and buy the card',
        safeMsg: 'Exactly right. Asking to keep secrets from parents and asking for money are huge red flags.',
        riskyMsg: 'This is a trick called grooming or a romance scam. Someone who cares about you wouldn’t ask for secrets or money.',
        lesson: '“Don’t tell your parents” + “send money or gifts” = stop, block and tell an adult. It is never your fault.',
      },
      privatePhoto: {
        author: '“Talent Scout ⭐”',
        tag: '⚠️ Request',
        content: 'You could be a model! Send me some private photos of yourself, just for me. I’ll make you famous!',
        safe: 'Never send — block, report, tell an adult',
        risky: 'Send a photo to become famous',
        safeMsg: 'Perfect. Never send private photos to anyone online. Block, report and tell a parent or teacher.',
        riskyMsg: 'Stop! Photos can be copied and used to hurt or threaten you. Tell a trusted adult right away — you won’t be in trouble.',
        lesson: 'Real scouts never ask kids for private photos. If someone does, or threatens you, tell an adult and report it.',
      },
      fakeNews: {
        author: 'Page “Health Secrets 100%”',
        tag: '⚠️ Viral post',
        content: 'SHARE NOW!!! Eating 10 lemons a day stops ALL illnesses! Doctors hate this trick!!!',
        safe: 'Check a reliable source before sharing',
        risky: 'Share it with everyone',
        safeMsg: 'Smart! Check trusted sources (doctors, official health sites) before you share.',
        riskyMsg: 'Sharing false health tips can hurt people. “Doctors hate this trick” is a classic fake-news phrase.',
        lesson: 'Ask: Who wrote it? Is there proof? Do trusted sources say the same? If not — don’t share.',
      },
      phishing: {
        author: '“PixelBook Security”',
        tag: '⚠️ Message',
        content: 'Your account will be DELETED in 1 hour! Log in here to keep it: pixe1book-login.co',
        safe: 'Ignore it and report the message',
        risky: 'Click and log in quickly',
        safeMsg: 'Right! Look closely: “pixe1book” uses a number 1. Real platforms don’t threaten you like this.',
        riskyMsg: 'That was a fake login page stealing your password. Change your password and tell an adult.',
        lesson: 'Urgent threats + odd links = phishing. Open the real app yourself instead of clicking.',
      },
      easyMoney: {
        author: '“Easy Job Agency”',
        tag: '⚠️ Job offer',
        content: 'Students earn NT$3000 a day from home! Just lend us your bank account and phone number.',
        safe: 'Refuse and tell an adult',
        risky: 'Sounds great, send my account',
        safeMsg: 'Good call. Lending your account can make you part of a crime without knowing it.',
        riskyMsg: 'Scammers use kids’ accounts to move stolen money. Never lend accounts, cards or phone numbers.',
        lesson: 'If a job sounds too easy to be true, it is a scam. Never share bank details.',
      },
    },
  },
};
